import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaFilter, FaSearch, FaDownload } from 'react-icons/fa';
import { authAPI } from '../../services/api';

export default function TransactionHistoryPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toast, setToast] = useState({ show: false, type: '', message: '' });

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  // Fetch transactions from API
  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const result = await authAPI.getExpertTransactions();
      
      if (result.success) {
        setTransactions(result.data || []);
      } else {
        showToast('error', result.message || 'Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showToast('error', 'Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      upcoming: {
        className: 'status-upcoming',
        text: 'Upcoming'
      },
      completed: {
        className: 'status-completed',
        text: 'Completed'
      },
      cancelled: {
        className: 'status-cancelled',
        text: 'Cancelled'
      },
      pending: {
        className: 'status-pending',
        text: 'Pending'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`status-badge ${config.className}`}>
        {config.text}
      </span>
    );
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = 
      transaction.client_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.transaction_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.service?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="transaction-history-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading transaction history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-history-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification ${toast.type === 'success' ? 'toast-success' : 'toast-error'}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="transaction-header">
        <div className="header-content">
          <h1>Transaction History</h1>
          <p>View all your consultation transactions and payments</p>
        </div>

      {/*   <button className="export-btn">
          <FaDownload size={16} />
          Export
        </button> */}
      </div>

      {/* Filters */}
      <div className="transaction-filters">
        <div className="search-container">
          <FaSearch size={16} />
          <input
            type="text"
            placeholder="Search by client name, transaction ID, or service..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-container">
          <FaFilter size={16} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="transaction-table-container">
        {filteredTransactions.length === 0 ? (
          <div className="empty-state">
            <FaCalendarAlt size={48} className="empty-icon" />
            <h3>No transactions found</h3>
            <p>
              {transactions.length === 0
                ? "You haven't had any transactions yet."
                : "No transactions match your current filters."
              }
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="transaction-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.transaction_id} className="transaction-row">
                    <td>
                      <div className="transaction-id">
                        {transaction.transaction_id}
                      </div>
                    </td>
                    <td>
                      <div className="date-cell">
                        <FaCalendarAlt size={14} />
                        {formatDate(transaction.date)}
                      </div>
                    </td>
                    <td>
                      <div className="client-cell">
                        {transaction.client_description}
                      </div>
                    </td>
                    <td>
                      <div className="service-cell">
                        {transaction.service}
                      </div>
                    </td>
                    <td>
                      <div className="amount-cell">
                        {formatCurrency(transaction.amount)}
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(transaction.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style jsx>{`
   

        .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
        }

        .header-content h1 {
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .header-content p {
          margin: 0;
          color: #666;
          font-size: 16px;
        }

        .export-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #0e4410;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .export-btn:hover {
          background: #0a330a;
          transform: translateY(-1px);
        }

        .transaction-filters {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .search-container, .filter-container {
          display: flex;
          align-items: center;
          gap: 8px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px;
        }

        .search-container {
          flex: 1;
          min-width: 250px;
        }

        .search-input, .filter-select {
          border: none;
          outline: none;
          background: transparent;
          width: 100%;
          font-size: 14px;
        }

        .filter-select {
          min-width: 120px;
          cursor: pointer;
        }

        .transaction-table-container {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .transaction-table {
          width: 100%;
          border-collapse: collapse;
        }

        .transaction-table th {
          background: #f9fafb;
          padding: 16px 20px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .transaction-table td {
          padding: 16px 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .transaction-row:hover {
          background: #f9fafb;
        }

        .transaction-id {
          font-family: monospace;
          font-size: 14px;
          color: #374151;
          font-weight: 500;
        }

        .date-cell {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #374151;
        }

        .client-cell {
          font-weight: 500;
          color: #1a1a1a;
        }

        .service-cell {
          color: #666;
          text-transform: capitalize;
        }

        .amount-cell {
          font-weight: 600;
          color: #1a1a1a;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-upcoming {
          background: #fef3c7;
          color: #f59e0b;
        }

        .status-completed {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-cancelled {
          background: #fee2e2;
          color: #ef4444;
        }

        .status-pending {
          background: #dbeafe;
          color: #3b82f6;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          color: #374151;
        }

        .empty-state p {
          margin: 0;
          color: #6b7280;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #0e4410;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1001;
          border-radius: 8px;
          padding: 16px 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease-out;
        }

        .toast-success {
          background: #22c55e;
          color: white;
        }

        .toast-error {
          background: #ef4444;
          color: white;
        }

        .toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .toast-icon {
          font-weight: bold;
          font-size: 16px;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .transaction-history-container {
            padding: 16px;
          }

          .transaction-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .transaction-filters {
            flex-direction: column;
          }

          .search-container {
            min-width: auto;
          }

          .transaction-table {
            font-size: 14px;
          }

          .transaction-table th,
          .transaction-table td {
            padding: 12px 16px;
          }
        }
      `}</style>
    </div>
  );
}